import { useCallback, useEffect, useRef } from 'react'

import { useQuery } from '@apollo/client'
import { ReviewAddIcon } from '@web/../assets/icons'

import Clickable from '@web/components/Clickable'
import FlickingArea from '@web/components/FlickingArea/FlickingArea'
import Loading from '@web/components/Loading'
import NewThemelistCard from '@web/components/ThemelistCard/NewThemelistCard'
import ThemelistCard, { ViewType } from '@web/components/ThemelistCard/ThemelistCard'
import { gql } from '@web/gql'
import { ThemelistsPageInfo } from '@web/gql/graphql'
import { useGoTo } from '@web/hooks/useGoTo'
import usePageInfo from '@web/hooks/usePageInfo'
import routes from '@web/libs/constants/routes'
import nClicks from '@web/libs/nlog/click/codes'
import { neloLogger } from '@web/libs/utils'

import NewThemelistButton from './NewThemelistButton'
import styles from './UserThemelists.module.scss'

export const UserThemelistsWithCountQuery = gql(`
  query UserThemelistsWithCount(
    $input: UserThemelistsInput!
    $connection: ThemelistsConnectionInput!
    $skipCountQuery: Boolean = false
  ) {
    userThemelists(input: $input, connection: $connection) {
      edges {
        ...UserThemelists_ThemelistEdge
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
    userThemelistsCount(input: $input) @skip(if: $skipCountQuery)
  }
`)

export const PAGE_SIZE = 10

const UserThemelists = () => {
  const { isMyPage, pageOwner } = usePageInfo()
  const { goTo } = useGoTo()

  const themelistsPageInfo = useRef<ThemelistsPageInfo | undefined>()

  const {
    data: themelistsData,
    loading,
    fetchMore,
    error: themelistsError,
  } = useQuery(UserThemelistsWithCountQuery, {
    variables: {
      input: {
        userIdno: pageOwner?.idno ?? '',
      },
      connection: {
        first: PAGE_SIZE,
      },
    },
    skip: !pageOwner?.idno,
    onError: error => {
      neloLogger('error', `리뷰탭 유저 테마리스트 오류 발생: ${error}`)
    },
  })

  useEffect(() => {
    themelistsPageInfo.current = themelistsData?.userThemelists.pageInfo
  }, [themelistsData?.userThemelists.pageInfo])

  const fetchMoreThemelists = useCallback(() => {
    if (!themelistsPageInfo.current?.hasNextPage) return

    fetchMore({
      variables: {
        input: { userIdno: pageOwner?.idno },
        connection: { first: PAGE_SIZE, after: themelistsPageInfo.current.endCursor },
        skipCountQuery: true,
      },
    })
  }, [pageOwner?.idno, fetchMore])

  if (loading) {
    return (
      <div className={styles.theme_area}>
        <h2 className="place_section_header">
          테마
          <span className={styles.count}>
            0<span className="place_blind">개</span>
          </span>
        </h2>
        {loading && <Loading height={270} />}
      </div>
    )
  }

  if (themelistsError) {
    return null
  }

  const themelistEdges = themelistsData?.userThemelists.edges
  const themelistsCount = themelistsData?.userThemelistsCount

  if (!isMyPage && !themelistEdges?.length) {
    return null
  }

  const showCreateButton = isMyPage && !!themelistsCount && themelistsCount > 1
  const showCreateCard = isMyPage && themelistsCount === 1

  const clickCode = isMyPage ? nClicks.reviewTab.MYTHEME : nClicks.reviewTabOthers.THEMELIST

  const goToThemelistNew = () => goTo(routes.THEME_NEW)

  return (
    <div className={styles.theme_area}>
      <h2 className="place_section_header">
        <div className={styles.place_section_header_title}>
          테마
          <span className={styles.count}>
            {themelistsCount ?? 0}
            <span className="place_blind">개</span>
          </span>
        </div>
        {showCreateButton && (
          <Clickable className={styles.btn} nClickCode={nClicks.reviewTab.NEWTHEME_2} onClick={goToThemelistNew}>
            <span className={styles.icon_wrapper}>
              <ReviewAddIcon className={styles.icon} />
            </span>
            만들기
          </Clickable>
        )}
      </h2>
      <div className={styles.section_content}>
        {themelistEdges?.length ? (
          <div className={styles.theme_list_area}>
            <FlickingArea
              className={styles.theme_list}
              arrowOption={{ moveCount: 2 }}
              options={{ cameraTag: 'ul', onNeedPanel: fetchMoreThemelists, needPanelThreshold: 100, align: 'prev' }}
              useReinitialize={false}
            >
              <>
                {themelistEdges?.map(themelistEdge => (
                  <ThemelistCard.component
                    key={themelistEdge.themelist.id}
                    viewType={ViewType.REVIEW_TAB}
                    themelist={themelistEdge.themelist}
                    clickCode={clickCode}
                  />
                ))}
                {showCreateCard && <NewThemelistCard isReviewTab clickCode={nClicks.reviewTab.NEWTHEME_2} />}
              </>
            </FlickingArea>
          </div>
        ) : (
          <NewThemelistButton onClick={goToThemelistNew} />
        )}
      </div>
    </div>
  )
}

export default UserThemelists

UserThemelists.fragments = {
  themelistEdge: gql(`
    fragment UserThemelists_ThemelistEdge on ThemelistEdge {
      themelist {
        id
        ...ThemelistCard_Themelist
      }
    }
  `),
}
